import {
  ICurricula,
  IDistrictCurricula,
  ISchoolCurricula,
  ISchoolGrouping,
  ISchoolGroupingMap,
} from 'infrastructure/api'
import { getCurriculumInDistrictsByDistrictID } from '../../tree/TreeActions'
import { useCallback } from 'react'
import { useGroupForPrimaryTreeId } from '../../../api/tree.hooks'
import { stringOrNumberSort } from '../../Helpers'
import { SchoolCourse } from '../../../api/school.atoms'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { allProductsByPrimaryTreeId } from 'api/masters.atoms'

async function getDistrictStuff(districtId: number) {
  const curricula = await getCurriculumInDistrictsByDistrictID(districtId)
  return {
    curricula,
  }
}

async function getSchoolStuff(bundledCurricula: ISchoolCurricula[] = []) {
  return {
    curricula: bundledCurricula,
  }
}

export function useGetDistrictGrouping() {
  const groupCurricula = useGroupCurricula()
  return useCallback(
    async (districtId: number) => {
      const { curricula } = await getDistrictStuff(districtId)
      return groupCurricula(curricula)
    },
    [groupCurricula],
  )
}

export function useGetSchoolGrouping(schoolId: string | number) {
  const groupCurricula = useGroupCurricula()
  return useCallback(
    async (bundledCurricula: SchoolCourse[]) => {
      const schoolCurricula = bundledCurricula.map((b) => ({
        ...b,
        schoolId: Number(schoolId),
        tree_version: b.version_id,
      }))
      const { curricula } = await getSchoolStuff(schoolCurricula)
      return groupCurricula(curricula)
    },
    [schoolId, groupCurricula],
  )
}

function useGroupCurricula() {
  const getGroup = useGroupForPrimaryTreeId()
  const productsByTreeId = useAtomValue(allProductsByPrimaryTreeId)
  return useCallback(
    (curricula: ICurricula[]) => {
      const grouping = curricula.reduce<ISchoolGroupingMap>(
        (acc, curriculum) => {
          const copies = [curriculum]
          const group = getGroup(curriculum.tree_id)
          const groupName = group?.name ?? 'NotGrouped'
          if (!acc[groupName]) {
            acc[groupName] = []
          }
          const product = productsByTreeId[curriculum.tree_id]
          if (product) {
            acc[groupName].push({
              master: product,
              curriculaCopies: copies,
            })
          }
          return acc
        },
        {},
      )

      const sortedKeys = Object.keys(grouping)
        .filter((k) => k !== 'Other')
        .sort(stringOrNumberSort)
      return { grouping, sortedKeys }
    },
    [getGroup, productsByTreeId],
  )
}

function groupingSort(a: ISchoolGrouping, b: ISchoolGrouping) {
  return a.master.title.localeCompare(b.master.title)
}

export function sortAndSeparateGrouping({
  curriculaCopies,
  ...g
}: ISchoolGrouping) {
  const notDeletedCopies: ISchoolGrouping[] = []
  const deletedCopies: ISchoolGrouping[] = []
  const { deleted, notDeleted } = curriculaCopies.reduce<{
    deleted: ICurricula[]
    notDeleted: ICurricula[]
  }>(
    ({ deleted, notDeleted }, c) => {
      if (c.deleted) {
        deleted.push(c)
      } else {
        notDeleted.push(c)
      }
      return { deleted, notDeleted }
    },
    { deleted: [], notDeleted: [] },
  )
  if (deleted.length) {
    deletedCopies.push({
      curriculaCopies: deleted,
      ...g,
    })
  }
  if (notDeleted.length) {
    notDeletedCopies.push({
      curriculaCopies: notDeleted,
      ...g,
    })
  }
  return { deletedCopies, notDeletedCopies }
}

export function sortGroupings(groupings: ISchoolGrouping[]) {
  const { deleted, notDeleted } = groupings.reduce<{
    deleted: ISchoolGrouping[]
    notDeleted: ISchoolGrouping[]
  }>(
    ({ deleted, notDeleted }, grouping) => {
      const { notDeletedCopies, deletedCopies } =
        sortAndSeparateGrouping(grouping)
      return {
        notDeleted: [...notDeleted, ...notDeletedCopies],
        deleted: [...deleted, ...deletedCopies],
      }
    },
    { deleted: [], notDeleted: [] },
  )
  deleted.sort(groupingSort)
  notDeleted.sort(groupingSort)
  return { deleted, notDeleted }
}

export function isSchoolCurricula(
  curricula: ICurricula,
): curricula is ISchoolCurricula {
  return 'schoolId' in curricula && !!curricula.schoolId
}

export function isDistrictCurricula(
  curricula: ICurricula,
): curricula is IDistrictCurricula {
  return 'districtId' in curricula && !!curricula.districtId
}
